/**
 * HOR-10: helpers for multi-image uploads (package/final control images).
 *
 * The admin intentionally does NOT compress images – the final-control shots should keep
 * their full detail. The backend accepts 1–10 images per POST /watchServices/:id/images
 * request and JSON bodies up to 50 MB, so files are validated locally and split into
 * chunks that stay safely below both limits.
 */

// mirrors the backend's per-image limit (10 MB binary)
export const MAX_ADMIN_IMAGE_BYTES = 10 * 1024 * 1024

// backend contract: at most 10 images per request
export const CHUNK_MAX_COUNT = 10

// base64 character budget per request – keeps the JSON body safely below the 50 MB limit
export const CHUNK_MAX_CHARS = 35 * 1024 * 1024

const TYPE_WHITELIST = /^image\/(png|jpe?g|webp|bmp)$/i

// some OS/browser combinations deliver an empty file.type – fall back to the extension
const EXTENSION_FALLBACK = /\.(png|jpe?g|webp|bmp)$/i

/**
 * Filter a file list down to uploadable images.
 *
 * @param {File[]} files
 * @returns {{ valid: File[], skippedInvalidType: number, skippedTooLarge: number }}
 */
export const validateFiles = files => {
  const valid = []
  let skippedInvalidType = 0
  let skippedTooLarge = 0

  for (const file of files) {
    const typeOk = file.type ? TYPE_WHITELIST.test(file.type) : EXTENSION_FALLBACK.test(file.name || '')

    if (!typeOk) {
      skippedInvalidType += 1
      continue
    }

    if (file.size > MAX_ADMIN_IMAGE_BYTES) {
      skippedTooLarge += 1
      continue
    }

    valid.push(file)
  }

  return { valid, skippedInvalidType, skippedTooLarge }
}

/**
 * Read files sequentially as base64 data URLs. Unreadable files are skipped and reported
 * via `skippedInvalidType` (same bucket as unsupported formats – for the user the file is
 * unusable either way).
 *
 * @param {File[]} files
 * @param {(done: number, total: number) => void} [onProgress]
 * @returns {Promise<{ items: { name: string, dataUrl: string }[], skippedInvalidType: number }>}
 */
export const readFilesAsDataUrls = async (files, onProgress) => {
  const items = []
  let skippedInvalidType = 0

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index]

    const dataUrl = await new Promise(resolve => {
      const reader = new FileReader()
      reader.addEventListener('load', e => resolve(e.target.result))
      reader.addEventListener('error', () => resolve(null))
      reader.readAsDataURL(file)
    })

    if (typeof dataUrl === 'string' && dataUrl.length > 0) items.push({ name: file.name, dataUrl })
    else skippedInvalidType += 1

    if (typeof onProgress === 'function') onProgress(index + 1, files.length)
  }

  return { items, skippedInvalidType }
}

/**
 * Split image items into upload chunks: at most CHUNK_MAX_COUNT items AND at most
 * CHUNK_MAX_CHARS total data-URL characters per chunk. A single oversized item (cannot
 * happen with the 10 MB file cap, kept defensively) gets a chunk of its own.
 *
 * @param {{ dataUrl: string }[]} items
 * @returns {Array<Array<{ dataUrl: string }>>}
 */
export const chunkImages = items => {
  const chunks = []
  let current = []
  let currentChars = 0

  for (const item of items) {
    const size = item.dataUrl?.length || 0

    if (current.length > 0 && (current.length >= CHUNK_MAX_COUNT || currentChars + size > CHUNK_MAX_CHARS)) {
      chunks.push(current)
      current = []
      currentChars = 0
    }

    current.push(item)
    currentChars += size
  }

  if (current.length > 0) chunks.push(current)

  return chunks
}
